import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 30000; // 30 seconds

export const useBookingNotifications = () => {
  const [permission, setPermission] = useState(Notification.permission);
  const [isPolling, setIsPolling] = useState(false);
  const lastBookingCountRef = useRef(null);
  const lastBookingIdRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const permissionRef = useRef(permission);

  // Keep a ref of the latest permission so showNotification can stay stable
  useEffect(() => {
    permissionRef.current = permission;
  }, [permission]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if ('Notification' in window) {
      try {
        const result = await Notification.requestPermission();
        setPermission(result);
        return result === 'granted';
      } catch (error) {
        console.error('Error requesting notification permission:', error);
        return false;
      }
    }
    return false;
  }, []);

  // Show notification (stable — reads permission via ref)
  const showNotification = useCallback((booking) => {
    if (permissionRef.current !== 'granted' || !booking) return;

    const title = '🚖 New Booking Request!';
    const body = `${booking.name} - ${booking.serviceType} (${booking.vehicleType})\n${booking.pickupLocation || 'N/A'} → ${booking.dropLocation || 'N/A'}`;

    const notification = new Notification(title, {
      body,
      icon: '/sg-cabs-logo.png',
      badge: '/sg-cabs-logo.png',
      tag: `booking-${booking.id}`,
      requireInteraction: false,
      silent: false,
      vibrate: [200, 100, 200],
      data: {
        bookingId: booking.id,
        url: '/admin/bookings'
      }
    });

    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      window.location.href = '/admin/bookings';
      notification.close();
    };

    // Auto-close after 10 seconds
    setTimeout(() => notification.close(), 10000);
  }, []);

  // Check for new bookings
  const checkNewBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings`);
      const bookings = response.data;

      if (bookings.length === 0) {
        lastBookingCountRef.current = 0;
        return;
      }

      // Get the latest booking (first in the sorted list)
      const latestBooking = bookings[0];

      // Initialize refs on first check
      if (lastBookingCountRef.current === null) {
        lastBookingCountRef.current = bookings.length;
        lastBookingIdRef.current = latestBooking.id;
        return;
      }

      // Check if there's a new booking
      const hasNewBooking = latestBooking.id !== lastBookingIdRef.current;

      if (hasNewBooking) {
        console.log('📬 New booking detected:', latestBooking.name);
        showNotification(latestBooking);
        lastBookingCountRef.current = bookings.length;
        lastBookingIdRef.current = latestBooking.id;
      }
    } catch (error) {
      console.error('Error checking for new bookings:', error);
    }
  }, [showNotification]);

  // Stop polling (stable — referenced by mount/unmount effect)
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      console.log('🔕 Stopping booking notification polling...');
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      setIsPolling(false);
    }
  }, []);

  // Start polling
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    console.log('🔔 Starting booking notification polling...');
    setIsPolling(true);

    // Initial check
    checkNewBookings();

    // Set up polling interval
    pollIntervalRef.current = setInterval(checkNewBookings, POLL_INTERVAL);
  }, [checkNewBookings]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    permission,
    requestPermission,
    startPolling,
    stopPolling,
    isPolling
  };
};
